import { Directive, ElementRef, HostListener, Input, Output, EventEmitter, Renderer2 } from '@angular/core';
import { NgControl } from '@angular/forms';
import { containsSqlInjection, containsXss, sanitizeInput } from './security-validators';

/**
 * Directive pour sécuriser les champs de saisie contre les injections SQL et XSS
 *
 * Usage:
 * <input type="text" secureInput />
 * <textarea secureInput></textarea>
 */
@Directive({
    selector: '[secureInput]'
})
export class SecureInputDirective {

    /**
     * Si true, affiche un message d'avertissement dans la console
     */
    @Input() logWarnings = false;

    /**
     * Émis quand une tentative d'injection est détectée
     */
    @Output() securityThreat = new EventEmitter<{ type: 'sql' | 'xss'; value: string }>();

    private hasSecurityError = false;

    constructor(
        private el: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
        private ngControl: NgControl,
        private renderer: Renderer2
    ) { }

    @HostListener('input', ['$event'])
    onInput(event: Event): void {
        const input = event.target as HTMLInputElement | HTMLTextAreaElement;
        const currentValue = input.value;

        // Vérifier SQL Injection ou XSS
        const hasSqlInjection = containsSqlInjection(currentValue);
        const hasXss = containsXss(currentValue);

        if (hasSqlInjection || hasXss) {
            // Afficher visuellement l'erreur (champ en rouge)
            if (!this.hasSecurityError) {
                this.showSecurityError();
                this.hasSecurityError = true;
            }

            const threatType = hasSqlInjection ? 'sql' : 'xss';
            this.handleThreat(threatType, currentValue);
        } else {
            // Retirer les indicateurs visuels si plus d'erreur
            if (this.hasSecurityError) {
                this.clearSecurityError();
                this.hasSecurityError = false;
            }
        }
    }

    @HostListener('blur')
    onBlur(): void {
        // Revérifier à la perte de focus
        const currentValue = this.el.nativeElement.value;
        const hasThreat = containsSqlInjection(currentValue) || containsXss(currentValue);

        if (hasThreat && !this.hasSecurityError) {
            this.showSecurityError();
            this.hasSecurityError = true;
        } else if (!hasThreat && this.hasSecurityError) {
            this.clearSecurityError();
            this.hasSecurityError = false;
        }
    }

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
        // Laisser le collage se faire, puis vérifier après un court délai
        setTimeout(() => {
            const currentValue = this.el.nativeElement.value;
            const hasThreat = containsSqlInjection(currentValue) || containsXss(currentValue);

            if (hasThreat) {
                this.showSecurityError();
                this.hasSecurityError = true;

                const threatType = containsSqlInjection(currentValue) ? 'sql' : 'xss';
                this.handleThreat(threatType, currentValue);
            }
        }, 10);
    }

    /**
     * Affiche visuellement l'erreur de sécurité (champ rouge)
     */
    private showSecurityError(): void {
        const element = this.el.nativeElement;

        // Ajouter la classe d'erreur de sécurité
        this.renderer.addClass(element, 'security-error');
    }

    /**
     * Retire les indicateurs visuels d'erreur de sécurité
     */
    private clearSecurityError(): void {
        const element = this.el.nativeElement;
        this.renderer.removeClass(element, 'security-error');
    }

    /**
     * Gère la détection d'une menace
     */
    private handleThreat(type: 'sql' | 'xss', value: string): void {
        this.securityThreat.emit({ type, value });

        if (this.logWarnings) {
            console.warn(`[SecureInput] Menace ${type.toUpperCase()} détectée:`, value.substring(0, 100));
        }
    }
}

/**
 * Directive pour afficher un message d'erreur de sécurité
 *
 * Usage:
 * <div *ngIf="form.get('field')?.errors?.securityViolation" appSecurityError>
 *   {{ form.get('field')?.errors?.securityViolation?.message }}
 * </div>
 */
@Directive({
    selector: '[appSecurityError]'
})
export class SecurityErrorDirective {
    constructor(private el: ElementRef) {
        // Appliquer le style d'erreur de sécurité
        const element = this.el.nativeElement as HTMLElement;
        element.style.color = '#dc3545';
        element.style.fontSize = '0.85rem';
        element.style.marginTop = '4px';
        element.style.display = 'flex';
        element.style.alignItems = 'center';
        element.style.gap = '6px';
    }
}
